<template>
  <div>
    <v-card elevation="0" flat>
      <div v-if="!isManager" class="w-100 card-border-red">
        <group-by-menu @change="groupByChanged" />
      </div>
      <v-card-text class="d-flex flex-wrap">
        <v-simple-table class="mt-4 my-table" dense>
          <template #default>
            <users-table-header
              :loading="users.loading"
              :users="users.data"
              :disable-period="!isManager"
              :column-title="filter.headerTitle"
              @periodChanged="periodUpdated"
              @sortedData="users.data = $event"
            />
            <tbody v-if="!users.loading" class="custom-white-table">
              <tr v-for="(user, index) in users.data" :key="index">
                <td>
                  <span class="d-flex">
                    <span
                      class="mt-3 mr-n2 font-weight-bold"
                      style="width: 25px"
                      >{{ index + 1 }}</span
                    >&nbsp;
                    <v-btn icon @click="getSalesByArea(user.uId)">
                      <v-icon color="#B71C1C">mdi-chart-pie</v-icon>
                    </v-btn>
                    <div
                      class="d-flex flex-column align-items-center justify-center text-caption"
                    >
                      <small class="mb-n2 text-black">{{
                        transformZoneid(user?.zoneId)
                      }}</small>
                      <sales-policy-file-downloader
                        class="mx-1"
                        icon-size="25"
                        policy-type="car"
                        :icon-class="
                          user?.hasCar > 0
                            ? 'car-policy-enabled'
                            : 'car-policy-disabled'
                        "
                      />
                    </div>
                    <v-icon :color="user?.trafficLight.color" size="15"
                      >mdi-circle</v-icon
                    >
                    <div class="">
                      <div
                        class="text-left font-weight-bold text-decoration-underline text-nowrap text-capitalize"
                      >
                        <a @click="showUser(user?.uId)">
                          <v-tooltip bottom>
                            <template #activator="{ on, attrs }">
                              <span v-bind="attrs" v-on="on">{{
                                user?.userNameShort
                              }}</span>
                            </template>
                            <span>{{ user?.userName }}</span>
                          </v-tooltip>
                        </a>
                      </div>
                      <small class="text-left text-uppercase text-nowrap">
                        <span class="text-black">{{ user?.uId }}</span> |
                        <span
                          class="text-capitalize text-decoration-underline font-weight-bold"
                        >
                          <v-tooltip bottom>
                            <template #activator="{ on, attrs }">
                              <span v-bind="attrs" v-on="on">{{
                                user?.ZoneDescriptionShort
                              }}</span>
                            </template>
                            <span>{{ user?.ZoneDescription }}</span>
                          </v-tooltip>
                        </span>
                      </small>
                    </div>

                    <div class="text-right ml-auto pl-3">
                      <b class="text-decoration-underline text-nowrap">
                        <small
                          >{{ formatNumber(user?.citizensCount, '', 0) }} h
                          <br />
                          {{
                            formatNumber(user?.citizensVsbilling, '$', 0)
                          }}&nbsp;S/h</small
                        >
                      </b>
                    </div>
                  </span>
                </td>

                <td class="pa-0">
                  <v-tooltip bottom>
                    <template #activator="{ on, attrs }">
                      <span v-bind="attrs" v-on="on">{{
                        formatNumber(user.salaryThisMonth, '$', 0)
                      }}</span>
                    </template>
                    <span class="font-weight-bold font-size-85"
                      >Més pasado:{{
                        formatNumber(user.salaryLastMonth, '$', 0)
                      }}</span
                    >
                  </v-tooltip>
                </td>
                <td class="pa-1">
                  <span
                    class="text-nowrap text-decoration-underline cursor-pointer"
                    @click="showIelPaymentModal(user.uId, user.zoneId)"
                  >
                    <small
                      >{{ formatFloat(user.ielPaidAmountPercentage, 0) }}% |
                      {{
                        formatFloat(user.ielPaidCustomerPercentage, 0)
                      }}%</small
                    >
                  </span>
                </td>

                <td class="pa-0 text-caption text-nowrap">
                  <span> {{ formatNumber(user.credit.limit, '$', 0) }} </span>
                </td>
                <td class="pa-0 text-caption text-nowrap">
                  <span> {{ formatNumber(user.credit.used, '$', 0) }} </span>
                </td>
                <td class="pa-0 text-caption text-nowrap">
                  <span> {{ formatNumber(user.credit.overdue, '$', 0) }} </span>
                </td>

                <td class="pa-1 text-nowrap">
                  <tooltip-avatar
                    v-if="user.promissories.valid > 0"
                    avatar-class="in-stock-bg text-white mx-1"
                    :text="user.promissories.valid"
                    tooltip-text="Activos"
                  />
                  <tooltip-avatar
                    v-if="user.promissories.inValidityPeriod > 0"
                    avatar-class="partial-stock-bg text-white mx-1"
                    :text="user.promissories.inValidityPeriod"
                    tooltip-text="En periodo de validez"
                  />
                  <tooltip-avatar
                    v-if="user.promissories.expired > 0"
                    avatar-class="no-stock-bg text-white mx-1"
                    :text="user.promissories.expired"
                    tooltip-text="Vencidos"
                  />
                  <tooltip-avatar
                    v-if="user.promissories.notFound > 0"
                    avatar-class="grey text-white text-caption mx-1"
                    :text="user.promissories.notFound"
                    tooltip-text="Sin capturar"
                  />
                </td>

                <td class="pa-0 text-caption">
                  <span class="text-black pa-0">
                    {{ formatNumber(user.customers.total, '', 0) }} /<br />
                    {{ formatNumber(user.customers.withPurchase, '', 0) }}
                    /<br />
                    {{ formatFloat(user.customers.coveragePercentage, 2) }}%
                    <br />
                  </span>
                </td>

                <td class="pa-0 text-caption text-nowrap">
                  {{
                    `${formatNumber(
                      user.customers.newCustomers,
                      '',
                      0
                    )} / ${formatNumber(
                      user.customers.billingNewCustomers,
                      '$',
                      0
                    )}`
                  }}
                  <br />
                  {{
                    formatNumber(user.customers.avgTicketNewCustomers, '$', 0)
                  }}
                </td>

                <td class="pa-0 text-caption">
                  <span>{{ formatNumber(user.assortment, '', 0) }}</span>
                </td>
                <td class="pa-0 text-caption">
                  <span
                    >{{ formatNumber(user.billingPncPercentage, '', 1) }}%</span
                  >
                </td>
                <td class="pa-0 text-caption">
                  <span
                    >{{ formatNumber(user.billingGnPercentage, '', 1) }}%</span
                  >
                </td>

                <td class="pa-0 text-caption">
                  <span>{{
                    formatNumber(user.promPurchaseLast6Months, '$', 0)
                  }}</span>
                </td>
                <td class="pa-1 text-caption">
                  <span>{{ formatNumber(user.budget, '$', 0) }}</span>
                </td>

                <td class="pa-1 text-caption">
                  <v-tooltip bottom>
                    <template #activator="{ on, attrs }">
                      <span
                        v-bind="attrs"
                        class="text-decoration-underline"
                        v-on="on"
                      >
                        {{ formatNumber(user.orders, '$', 0) }} <br />
                        {{ formatNumber(user.ordersVsBudgetPer, '', 0) }}%
                      </span>
                    </template>
                    <span class="font-weight-bold font-size-85"
                      >Pedidos del día anterior:
                      {{ formatNumber(user.yesterdayOrders, '$', 0) }}</span
                    >
                  </v-tooltip>
                  <br />
                </td>

                <td class="pa-1 text-caption">
                  <span>{{ formatNumber(user.billing, '$', 0) }} </span>
                  <br />
                  {{ formatNumber(user.billingVsBudgetPer, '', 2) }}%
                </td>

                <td class="pa-0 text-caption">
                  <div class="d-flex d-inline justify-content-center">
                    <tooltip-avatar
                      v-if="user.backorders.availableCount > 0"
                      avatar-class="in-stock-bg text-white"
                      :text="user.backorders.availableCount"
                      :tooltip-text="
                        formatNumber(user.backorders.availableAmount, '$', 0)
                      "
                    />
                    <tooltip-avatar
                      v-if="user.backorders.partialStockCount > 0"
                      avatar-class="partial-stock-bg text-white"
                      :text="user.backorders.partialStockCount"
                      :tooltip-text="
                        formatNumber(user.backorders.partialStockAmount, '$', 0)
                      "
                    />
                    <tooltip-avatar
                      v-if="user.backorders.noStockCount > 0"
                      avatar-class="no-stock-bg text-white"
                      :text="user.backorders.noStockCount"
                      :tooltip-text="
                        formatNumber(user.backorders.noStockAmount, '$', 0)
                      "
                    />
                    <tooltip-avatar
                      v-if="user.backorders.programmedCount > 0"
                      avatar-class="programmed-stock-bg text-white"
                      :text="user.backorders.programmedCount"
                      :tooltip-text="
                        formatNumber(user.backorders.programmedAmount, '$', 0)
                      "
                    />
                  </div>
                </td>

                <td class="pa-0 text-caption">
                  <a
                    class="text-decoration-underline text-black font-weight-bold"
                    @click="getCumulativeGrowth(user.uId)"
                  >
                    {{ formatFloat(user.cumulativeGrowth, 2) }}%
                  </a>
                </td>
                <td class="pa-0 text-caption">
                  <a
                    class="text-decoration-underline text-black font-weight-bold"
                    @click="billingVsEst(user.uId)"
                  >
                    {{ formatFloat(user.budgetCumulativeGrowth, 2) }}%
                  </a>
                </td>
              </tr>
            </tbody>
            <table-body-loader v-else :colspan="20" />
          </template>
        </v-simple-table>
        <hr />
        <alert-prominent
          v-if="error"
          :text="error"
          color="red"
          class="mb-2 w-100"
        />
      </v-card-text>
    </v-card>

    <graph
      v-model="salesByArea.modal"
      :char-title="salesByArea.charTitle"
      :loading="salesByArea.loading"
      :data="salesByArea.data"
      :errors="salesByArea.error"
      @onPeriodChange="updateSalesByArea"
    />

    <cumulative-growth
      v-model="cumulativeGrowth.modal"
      current-key-value="detail.current"
      old-key-value="detail.last"
      month-key-value="SPMON"
      amount-key-value="FACPER"
      total-current-key-value="sumBillingActual"
      total-old-key-value="sumBillingCompared"
      total-percent-key-value="cumulativeGrowth"
      :loading="cumulativeGrowth.loading"
      :data="cumulativeGrowth.data"
    />

    <billing-vs-estimate
      v-model="billVsEst.modal"
      :loading="billVsEst.loading"
      :data="billVsEst.data"
    />

    <customers-i-e-l-payment-table
      v-model="paymentModal.open"
      :period="period"
      :loading="paymentModal.loading"
      :data="paymentModal.data"
      :title="paymentModal.title"
    />
  </div>
</template>
<script>
  import moment from 'moment';
  import numbersFormats from '@/mixins/numbersFormats';
  import activeUsernameMixin from '@/mixins/activeUsernameMixin';
  import { MetaRoles, Roles } from '@/core/constants/roleConstants';
  import {
    getSalesByArea,
    getUsersSummary,
    getCumulativeGrowth,
    getBillingVsEstimate,
    getCustomersViaIelMonthly,
  } from '@/repositories/Daily';

  import UsersTableHeader from './UsersTableHeader.vue';
  import AlertProminent from '@/components/AlertProminent.vue';
  import TooltipAvatar from '@/components/TooltipAvatar.vue';
  import Graph from '@/views/daily/modals/Graph.vue';
  import BillingVsEstimate from '@/views/daily/modals/BillingVsEstimate.vue';
  import TableBodyLoader from '@/components/tables/TableBodyLoader.vue';

  import CumulativeGrowth from '@/components/modals/CumulativeGrowth.vue';
  import CustomersIELPaymentTable from '@/views/daily/modals/CustomersIELPaymentTable.vue';
  import SalesPolicyFileDownloader from '@/components/policies/SalesPolicyFileDownload.vue';
  import GroupByMenu from '@/views/daily/components/GroupByMenu.vue';

  export default {
    name: 'UsersList',
    components: {
      UsersTableHeader,
      AlertProminent,
      Graph,
      CumulativeGrowth,
      BillingVsEstimate,
      TableBodyLoader,
      CustomersIELPaymentTable,
      SalesPolicyFileDownloader,
      TooltipAvatar,
      GroupByMenu,
    },
    mixins: [numbersFormats, activeUsernameMixin],
    props: {
      userId: {
        default: '',
        type: String,
      },
      chnl: {
        required: true,
        type: String,
      },
    },
    data: () => ({
      username: '',
      users: {
        loading: false,
        data: [],
      },
      error: '',
      period: '',
      salesByArea: {
        seller: null,
        data: [],
        loading: false,
        charTitle: '',
        modal: false,
      },
      billVsEst: {
        modal: false,
        loading: false,
        data: null,
      },
      paymentModal: {
        open: false,
        title: '',
        loading: false,
        data: null,
      },
      cumulativeGrowth: {
        modal: false,
        loading: true,
        data: null,
      },
      filter: {
        groupBy: '',
        period: 0,
        headerTitle: 'Zona',
      },
    }),
    computed: {
      roleList() {
        const role = this.$route.meta.role;
        const listByGroup = {
          [Roles.SELLER]: Roles.SELLER,
          [Roles.MANAGER]:
            role === MetaRoles.RegionalManager ? Roles.MANAGER : Roles.SELLER,
          [Roles.REGIONAL_MANAGER]: Roles.REGIONAL_MANAGER,
        };
        return listByGroup[this.filter.groupBy] ?? '';
      },
      groupByOption() {
        const role = this.$route.meta.role;
        const options = {
          [Roles.SELLER]: 'A',
          [Roles.MANAGER]: role === MetaRoles.RegionalManager ? 0 : 'G',
          [Roles.REGIONAL_MANAGER]: role === MetaRoles.Subdirector ? 0 : 'T',
        };
        return options[this.filter.groupBy] ?? 0;
      },
      zoneIdLength() {
        const lengths = {
          [Roles.MANAGER]: 5,
          [Roles.REGIONAL_MANAGER]: 2,
          [Roles.DIRECTOR]: 1,
          [Roles.SUB_DIRECTOR]: 1,
        };
        return lengths[this.roleList] ?? 0;
      },
      isManager() {
        return this.$route.meta.role === MetaRoles.Manager;
      },
    },
    created() {
      this.username = this.userId ? this.userId : this.activeUsername?.username;
    },
    methods: {
      transformZoneid(zoneId) {
        if (!zoneId || !this.zoneIdLength) return zoneId;
        return String(zoneId).substring(0, this.zoneIdLength);
      },
      async getUsers() {
        this.users.loading = true;
        this.error = '';
        try {
          this.users.data = await getUsersSummary(
            this.username,
            this.chnl,
            this.period,
            this.groupByOption
          );
        } catch (error) {
          this.error =
            'Hubo un error al cargar la información. Por favor intente más tarde';
        }
        this.$emit('desactiveLoader');
        this.users.loading = false;
      },
      async getSalesByArea(user, period = null) {
        this.salesByArea.charTitle = `Participación por division`;
        this.salesByArea.seller = user;
        this.salesByArea.loading = true;
        this.salesByArea.modal = true;
        period = period ? period : this.getCurrentPeriod();
        try {
          this.salesByArea.data = await getSalesByArea(
            user,
            this.chnl,
            period.quarter,
            period.year
          );
        } catch (error) {
          this.salesByArea.modal = false;
          this.$toast.error(
            'Hubo problemas al cargar la participación por división, por favor intente más tarde.'
          );
        }
        this.salesByArea.loading = false;
      },
      showUser(user) {
        const currentRole = this.$route.meta.role;
        switch (currentRole) {
          case MetaRoles.Manager:
            this.$setLocalStorageSeller(user);
            this.$router.push({ name: 'SellerResults' });
            break;
          case MetaRoles.RegionalManager:
          case MetaRoles.Director:
          case MetaRoles.Subdirector:
            this.resolveUser(user);
            break;
          default:
            this.$toast.error('Rol de usuario no reconocido');
        }
      },
      //get the current quarter and year using the current date
      getCurrentPeriod() {
        const now = moment();
        return {
          quarter: now.quarter(),
          year: now.year(),
        };
      },
      updateSalesByArea(period) {
        this.salesByArea.year = period.year;
        this.salesByArea.quarter = period.quarter;
        this.getSalesByArea(this.salesByArea.seller.uId, period.division);
      },
      periodUpdated(newPeriod) {
        this.period = newPeriod;
        this.getUsers();
      },
      groupByChanged(newFilter) {
        this.filter = newFilter;
        this.periodUpdated(newFilter.period);
      },
      async getCumulativeGrowth(user) {
        this.cumulativeGrowth.modal = true;
        this.cumulativeGrowth.loading = true;
        try {
          this.cumulativeGrowth.data = await getCumulativeGrowth(
            user,
            this.chnl,
            1
          );
        } catch (error) {
          this.$toast.error(
            'No se pudo cargar el crecimiento acumulado de la zona'
          );
          this.cumulativeGrowth.modal = false;
          this.cumulativeGrowth.data = null;
        }
        this.cumulativeGrowth.loading = false;
      },
      async billingVsEst(user) {
        this.billVsEst.loading = true;
        this.billVsEst.modal = true;
        try {
          this.billVsEst.data = await getBillingVsEstimate(
            user,
            this.chnl,
            this.period
          );
        } catch (error) {
          this.billVsEst.modal = false;
          this.$toast.error('No se ha podido obtener la información');
        } finally {
          this.billVsEst.loading = false;
        }
      },

      async showIelPaymentModal(user, zone) {
        this.paymentModal.loading = true;
        this.paymentModal.open = true;
        this.paymentModal.title = `Detalle de pagos en IEL de la zona ${zone}`;
        try {
          this.paymentModal.data = await getCustomersViaIelMonthly(
            user,
            this.chnl,
            this.period
          );
        } catch (error) {
          this.$toast.error(
            'No se pudo cargar la información de pagos IEL de la zona'
          );
          this.paymentModal.open = false;
        }
        this.paymentModal.loading = false;
      },
      resolveUser(userId) {
        let config = {};
        switch (this.filter.groupBy) {
          case Roles.SELLER:
            config.routeName = 'SellerResults';
            config.storageName = 'currentSellerCode';
            break;
          case Roles.MANAGER:
            config.routeName = 'ManagerHome';
            config.storageName = 'currentManagerCode';
            break;
          case Roles.REGIONAL_MANAGER:
            config.routeName = 'DailyRegionalManager';
            config.storageName = 'currentRegionalCode';
            break;
          case Roles.DIRECTOR:
          case Roles.SUB_DIRECTOR:
            config.routeName = 'DailyDirector';
            config.storageName = 'currentDirectorCode';
            break;
        }
        localStorage.setItem(config.storageName, JSON.stringify(userId));
        this.$router.push({ name: config.routeName });
      },
    },
  };
</script>
